import { Field, Form, Formik } from "formik";
import { ScrollToFirstError } from "utils";
import { Button, SelectComponent, Textarea, Checkbox } from "shared";
import { Input, DropFileInput } from "shared";
import { InvestmentItem } from "../../../types";
import { ArtworkValues } from "./types";
import { artworkSchema } from "./validationSchemas";
import { RELIANCE_CONSENT_LABEL } from "./constants";

interface ArtworkFormProps {
  item: InvestmentItem;
  onSubmit: (values: ArtworkValues) => void;
  onCancel: () => void;
  isPending: boolean;
  currencyOptions: any[];
  onRemovedPhoto: (file: { name: string; url: string }) => void;
  onRemovedContract: (file: { name: string; url: string }) => void;
}

const ArtworkForm = ({
  item,
  onSubmit,
  onCancel,
  isPending,
  currencyOptions,
  onRemovedPhoto,
  onRemovedContract,
}: ArtworkFormProps) => {
  const existingContractFiles = (item.files ?? []).filter(
    (f: any) => f.role === "contract",
  );
  const existingPhotos = (item.files ?? []).filter(
    (f: any) => f.role === "photo",
  );

  const initialValues: ArtworkValues = {
    artworkType: String(item.details?.artworkType ?? ""),
    primaryValue:
      item.primaryValue !== null && item.primaryValue !== undefined
        ? String(item.primaryValue)
        : "",
    currencyUuid: item.currencyUuid ?? "",
    name: item.name ?? "",
    unknownAuthor: item.details?.author === "Autor nieznany",
    author: String(item.details?.author ?? ""),
    eraAge: String(item.details?.eraAge ?? ""),
    additionalInfo: item.additionalInfo ?? "",
    photoFiles: [],
    contractFile: null,
    relianceCanView: Boolean(item.relianceCanView ?? false),
  };

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={artworkSchema}
      onSubmit={onSubmit}
      enableReinitialize
    >
      {(formik) => (
        <Form>
          <ScrollToFirstError />
          <div className="modal-form__fields">
            <Field
              as={Input}
              name="artworkType"
              label="Rodzaj"
              type="text"
              error={
                (formik.touched.artworkType || formik.submitCount > 0) &&
                formik.errors.artworkType
                  ? formik.errors.artworkType
                  : undefined
              }
            />

            <Field
              as={Input}
              name="primaryValue"
              label="Cena zakupu"
              type="number"
              step="any"
              precision={2}
              maxLength={9}
              error={
                (formik.touched.primaryValue || formik.submitCount > 0) &&
                formik.errors.primaryValue
                  ? formik.errors.primaryValue
                  : undefined
              }
            />

            <Field
              as={SelectComponent}
              name="currencyUuid"
              label="Waluta"
              options={currencyOptions}
              isSearchable={true}
              error={
                (formik.touched.currencyUuid || formik.submitCount > 0) &&
                formik.errors.currencyUuid
                  ? formik.errors.currencyUuid
                  : undefined
              }
            />

            <Field as={Input} name="name" label="Tytuł" type="text" optional />

            <Field
              as={Checkbox}
              name="unknownAuthor"
              label="Autor nieznany"
              checked={formik.values.unknownAuthor}
              onChange={() => {
                const newVal = !formik.values.unknownAuthor;
                formik.setFieldValue("unknownAuthor", newVal);
                formik.setFieldValue("author", newVal ? "Autor nieznany" : "");
              }}
            />

            <Field
              as={Input}
              name="author"
              label="Autor"
              type="text"
              disabled={formik.values.unknownAuthor}
              optional={formik.values.unknownAuthor}
              error={
                !formik.values.unknownAuthor &&
                (formik.touched.author || formik.submitCount > 0) &&
                formik.errors.author
                  ? formik.errors.author
                  : undefined
              }
            />

            <Field
              as={Input}
              name="eraAge"
              label="Epoka/Wiek"
              type="text"
              optional
            />

            <Field
              component={Textarea}
              name="additionalInfo"
              label="Dodatkowe informacje"
              optional
            />

            <Field
              label="Zdjęcia"
              name="photoFiles"
              id="photoFiles"
              as={DropFileInput}
              accept=".png,.jpg,.jpeg"
              multiple={10}
              maxSize={5}
              description="Maksymalnie 10 plików, maks. 5 MB/plik (JPG, PNG)"
              optional
              showPreview
              existingFiles={
                existingPhotos.length > 0
                  ? existingPhotos
                      .filter((f: any) => f?.file?.name && f?.file?.url)
                      .map((f: any) => ({
                        name: f.file.name,
                        url: f.file.url,
                      }))
                  : undefined
              }
              onServerFileRemoved={onRemovedPhoto}
            />

            <Field
              label="Umowa"
              name="contractFile"
              id="contractFile"
              as={DropFileInput}
              accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
              optional
              multiple={10}
              maxSize={5}
              description="Maksymalnie 10 plików, maks. 5 MB/plik (PDF, DOC, DOCX, JPG, PNG)"
              existingFiles={existingContractFiles.map((f: any) => ({
                name: f.file?.name || "Plik",
                url: f.file?.url || "",
              }))}
              onServerFileRemoved={onRemovedContract}
            />

            <Field
              as={Checkbox}
              name="relianceCanView"
              label={RELIANCE_CONSENT_LABEL}
              checked={formik.values.relianceCanView}
              onChange={() =>
                formik.setFieldValue(
                  "relianceCanView",
                  !formik.values.relianceCanView,
                )
              }
            />
          </div>

          <div className="modal-buttons">
            <Button
              type="button"
              className="button--stroke button--rounded"
              onClick={onCancel}
              label="Anuluj"
            />
            <Button
              type="submit"
              className="button--black button--rounded"
              onClick={() => {}}
              label={isPending ? "Zapisywanie..." : "Zapisz"}
              isLoading={isPending}
            />
          </div>
        </Form>
      )}
    </Formik>
  );
};

export default ArtworkForm;
